import { computed } from "vue";
import { orderShorthandsFirst } from "./css";
import { fitDecorationFonts } from "./fonts";
import { ownValue } from "./record";
import { sanitizeDecoration } from "./sanitize";
import { elementSchemaOf } from "./schema";
import type {
	Decoration,
	DecorationBinding,
	DecorationCss,
	DecorationElements,
	DecorationGridLayout,
	DecorationIcon,
	DecorationInsert,
	DecorationStates,
	DecorationText,
} from "./types";

export { isRecord } from "./record";

export interface DecorScreen {
	visible: (name: string, fallback?: boolean) => boolean;
	variant: (name: string, fallback: string) => string;
	icon: (name: string) => DecorationIcon | null;
	bind: (name: string, states?: DecorationStates) => DecorationBinding;
	inserts: (name: string) => readonly DecorationInsert[];
	text: (text: DecorationText) => string;
	layout: (name: string, section: string) => DecorationGridLayout | null;
	limit: (name: string, fallback: number) => number;
}

const NO_ELEMENTS: DecorationElements = {};

const NO_BINDING: DecorationBinding = Object.freeze({});

const NO_INSERTS: readonly DecorationInsert[] = Object.freeze([]);

const HIDDEN: DecorationCss = Object.freeze({ display: "none !important" });

const NO_FAMILIES: readonly string[] = Object.freeze([]);

const noFamilies = () => NO_FAMILIES;

const NO_LANGUAGES: readonly string[] = Object.freeze([]);

const noLanguages = () => NO_LANGUAGES;

const textIn = (text: DecorationText, languages: readonly string[]) =>
	[...languages.map((language) => ownValue(text, language)), ...Object.values(text)].find((value) => !!value) ?? "";

const bindingCaches = new WeakMap<DecorationElements, WeakMap<readonly string[], Map<string, DecorationBinding>>>();

const elementsOf = (decoration: Decoration | null, screen: string): DecorationElements => ownValue(decoration?.screens, screen) ?? NO_ELEMENTS;

const activeStatesOf = (screen: string, name: string, states: DecorationStates): string[] => {
	const active = Object.keys(states).filter((state) => states[state]);
	const declared = elementSchemaOf(screen, name)?.css ?? [];
	return [...declared.filter((state) => active.includes(state)), ...active.filter((state) => !declared.includes(state))];
};

const mergeStyles = (styles: Array<DecorationCss | undefined>): DecorationCss | undefined => {
	const present = styles.filter((style): style is DecorationCss => !!style);
	return present.length > 1 ? Object.assign({}, ...present) : present[0];
};

const createBinding = (elements: DecorationElements, families: readonly string[], screen: string, name: string, states: string[]): DecorationBinding => {
	const layers = [name, ...states.map((state) => `${name}_${state}`)].map((key) => ownValue(elements, key)?.css);
	const hidden = ownValue(elements, name)?.visible === false ? HIDDEN : undefined;
	const style = fitDecorationFonts(orderShorthandsFirst(mergeStyles([...layers, hidden])), families);
	return { "data-el": name, "data-screen": screen, ...(states.length > 0 ? { "data-decor-state": states.join(" ") } : {}), style };
};

const bindingCacheOf = (elements: DecorationElements, families: readonly string[]) => {
	let byFamilies = bindingCaches.get(elements);
	if (!byFamilies) {
		byFamilies = new WeakMap();
		bindingCaches.set(elements, byFamilies);
	}

	let cache = byFamilies.get(families);
	if (!cache) {
		cache = new Map();
		byFamilies.set(families, cache);
	}
	return cache;
};

const bindingOf = (elements: DecorationElements, families: readonly string[], screen: string, name: string, states: string[]): DecorationBinding => {
	const cache = bindingCacheOf(elements, families);
	const key = [screen, name, ...states].join(" ");
	let binding = cache.get(key);
	if (!binding) {
		binding = createBinding(elements, families, screen, name, states);
		cache.set(key, binding);
	}
	return binding;
};

export const nullDecor: DecorScreen = {
	visible: (_name, fallback = true) => fallback,
	variant: (_name, fallback) => fallback,
	icon: () => null,
	bind: () => NO_BINDING,
	inserts: () => NO_INSERTS,
	text: (text) => textIn(text, NO_LANGUAGES),
	layout: () => null,
	limit: (_name, fallback) => fallback,
};

export const createDecorScreen = (
	read: () => Decoration | null | undefined,
	screenName?: string | null,
	families: () => readonly string[] = noFamilies,
	languages: () => readonly string[] = noLanguages,
): DecorScreen => {
	if (!screenName) return nullDecor;

	const elements = computed(() => elementsOf(sanitizeDecoration(read()), screenName));
	const element = (name: string) => ownValue(elements.value, name);

	return {
		visible: (name, fallback = true) => element(name)?.visible ?? fallback,
		variant: (name, fallback) => element(name)?.variant ?? fallback,
		icon: (name) => element(name)?.icon ?? null,
		bind: (name, states = {}) => bindingOf(elements.value, families(), screenName, name, activeStatesOf(screenName, name, states)),
		inserts: (name) => element(name)?.inserts ?? NO_INSERTS,
		text: (text) => textIn(text, languages()),
		layout: (name, section) => {
			const current = element(name);
			const key = ownValue(current?.sections, section);
			return (key && ownValue(current?.layouts, key)) || null;
		},
		limit: (name, fallback) => element(name)?.limit ?? fallback,
	};
};
